import { DateTime } from 'luxon';
import type { ComponentProps } from 'react';
import Text from '@/components/UI/Text';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import Box from '../UI/Box';
import Icon from '../Unistyles/Icon';
import Pressable from '../Unistyles/Pressable';

export const DatetimeFragment = graphql(
  /* GraphQL */ `
    fragment EventDetailInfoDatetime on event {
      datetime
    }
  `,
);

export const NotesFragment = graphql(
  /* GraphQL */ `
    fragment EventDetailInfoNotes on event {
      notes
    }
  `,
);

export const LocationFragment = graphql(
  /* GraphQL */ `
    fragment EventDetailInfoLocation on event {
      location
    }
  `,
);

export function Datetime({
  fragment,
}: {
  fragment: FragmentOf<typeof DatetimeFragment> | null | undefined;
}) {
  const event = readFragment(DatetimeFragment, fragment);
  const datetime = DateTime.fromISO(event?.datetime ?? '');

  return (
    <Box alignItems="center" direction="horizontal" space="lg" p="lg">
      <Icon name="time-outline" size="3xl" />
      <Box flex minWidth={0} space="xs">
        <Box direction="horizontal" space="sm">
          <Text size="2xl">{datetime.toFormat('EEE').toUpperCase()}</Text>
          <Text size="2xl">{datetime.toFormat('LLL d').toUpperCase()}</Text>
        </Box>
        <Text fontWeight="semibold" size="2xl">
          {datetime.toFormat('h:mm')} {datetime.toFormat('a')}
        </Text>
      </Box>
    </Box>
  );
}

export function Notes({
  fragment,
  ...props
}: {
  fragment: FragmentOf<typeof NotesFragment> | null | undefined;
} & ComponentProps<typeof Pressable>) {
  const event = readFragment(NotesFragment, fragment);

  return (
    <Pressable {...props}>
      <Box alignItems="center" direction="horizontal" space="lg" p="lg">
        <Box alignItems="center" direction="horizontal" space="lg" flex>
          <Icon name="information-circle-outline" size="3xl" />
          <Text numberOfLines={3} size="xl">
            {event?.notes}
          </Text>
        </Box>
        <Icon name="chevron-forward" size="sm" variant="typographySecondary" />
      </Box>
    </Pressable>
  );
}

export function Location({
  fragment,
  ...props
}: {
  fragment: FragmentOf<typeof LocationFragment> | null | undefined;
} & ComponentProps<typeof Box>) {
  const event = readFragment(LocationFragment, fragment);

  return (
    <Box
      alignItems="center"
      direction="horizontal"
      space="lg"
      p="lg"
      {...props}
    >
      <Icon name="location-outline" size="3xl" />
      <Box flex minWidth={0}>
        <Text numberOfLines={1} size="xl">
          {event?.location}
        </Text>
      </Box>
    </Box>
  );
}
